import { useState } from "react";
import { Lock, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/layout/Layout";
import { useEntitlements } from "@/context/EntitlementsContext";

export default function UpgradePrompt({ onDismiss }) {
  const { startTrial, refreshEntitlements } = useEntitlements();
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState("");

  if (dismissed) {
    return null;
  }

  const handleTrial = async () => {
    setSubmitting(true);
    setWaitlistMessage("");
    try {
      const data = await startTrial();
      if (data.status === "waitlist") {
        setWaitlistMessage(data.message || "Trial slots are full. Contact us on LINE to get early access.");
      } else {
        await refreshEntitlements();
        toast.success("ROOH Core trial started");
        window.location.reload();
      }
    } catch (err) {
      toast.error(err.message || "Could not start trial");
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <Layout>
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="upgrade-prompt">
        <Card className="w-full max-w-lg border-primary/20 bg-card">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Unlock ROOH Core</h1>
                  <p className="mt-1 text-sm text-muted-foreground">฿349/month or ฿3,490/year</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={dismiss} aria-label="Maybe later">
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              Start your 3-month free trial. No credit card required.
            </div>

            {waitlistMessage && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
                {waitlistMessage}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleTrial} disabled={submitting} className="gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Start Free Trial
              </Button>
              <Button variant="outline" onClick={dismiss}>Maybe later</Button>
            </div>

            <p className="text-xs text-muted-foreground">
              To upgrade manually, transfer via PromptPay and contact us on LINE with your receipt. We will activate your account within 24 hours.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
